import type { ComponentType } from 'react';

import {
  Permission,
  hasAnyPermission,
  hasPermission,
  type EnforcedPlanFeature,
} from '@bookora/shared';
import type { IconName } from '@bookora/ui';

import { sectionTabFeatures } from '@/features/plan/section-features';

/**
 * ════════════════════════════════════════════════════════════════════════════
 *  THE SECTION REGISTRY — one list, three consumers
 * ════════════════════════════════════════════════════════════════════════════
 *
 * `app/router.tsx` turns this into routes, `layout/sidebar.tsx` turns it into
 * navigation, and `layout/breadcrumbs.tsx` turns it into the trail. Adding a
 * screen means editing this file and nothing else in the shell.
 *
 * ── `status` ────────────────────────────────────────────────────────────────
 *
 *   'ready'    the section has a real screen. It appears in the sidebar for
 *              anyone holding its permission, and its route renders `load()`.
 *   'pending'  the route exists so a deep link resolves to an explanation
 *              rather than a 404, but the section is DELIBERATELY ABSENT FROM
 *              THE NAVIGATION. A menu entry that leads nowhere is worse than no
 *              entry: staff learn to distrust the whole sidebar.
 *
 * ── ADDING A SCREEN (the whole checklist) ───────────────────────────────────
 *
 *   1. Create `src/features/<key>/<key>-page.tsx` with a default export.
 *   2. Here: set `status: 'ready'` and
 *      `load: () => import('@/features/<key>/<key>-page')`.
 *   3. Nothing else. The nav, the route, the breadcrumb and the permission gate
 *      all follow from this entry.
 *
 * Child routes (`/patients/:id`, `/patients/new`) belong in the feature folder:
 * export a `routes` array from `src/features/<key>/routes.tsx` and add it to
 * `EXTRA_ROUTES` in `router.tsx`. They are not listed here, because this list
 * is the NAVIGATION model, and a detail page is not a navigation destination.
 *
 * ── PERMISSIONS ─────────────────────────────────────────────────────────────
 *
 * `permission` / `anyOf` decide whether the entry is DRAWN and whether the
 * route renders its screen or a 403. Both are affordances — every endpoint the
 * screen calls is independently guarded on the server. See `permissions/use-can.ts`.
 *
 * ── PLAN FEATURES (owner D6) ────────────────────────────────────────────────
 *
 * A plan never HIDES an entry. `planFeatures` lists the features of the
 * section's plan-locked tabs, read from `features/plan/section-features.ts`
 * (the one map) rather than typed here; the sidebar draws «پلن بالاتر» beside
 * the entry while at least one of them is locked. `planFeature` gates a whole
 * route: the router renders the locked screen in place of the section (none
 * does today — every plan-locked screen is a tab of a section that also holds
 * open ones).
 */

export type SectionStatus = 'ready' | 'pending';

export type NavGroupKey =
  | 'overview'
  | 'care'
  | 'operations'
  | 'finance'
  | 'engagement'
  | 'administration';

export interface NavGroup {
  key: NavGroupKey;
  /** Persian heading above the group in the sidebar. */
  label: string;
}

/**
 * Grouped by a clinic's day rather than by module: what the desk opens every
 * morning first («امروز» — the dashboard, the calendar, the till), then the
 * patients, then what the clinic is made of (services, packages, stock, people),
 * the books, talking to patients, and settings last.
 */
export const NAV_GROUPS: readonly NavGroup[] = [
  { key: 'overview', label: 'امروز' },
  { key: 'care', label: 'بیماران و درمان' },
  { key: 'operations', label: 'کلینیک' },
  { key: 'finance', label: 'مالی' },
  { key: 'engagement', label: 'ارتباط با بیماران' },
  { key: 'administration', label: 'مدیریت' },
];

export interface SectionDef {
  key: string;
  /** Absolute path under the app shell. */
  path: string;
  label: string;
  /**
   * One Persian line: the subtitle of the section's placeholder page
   * (`section-placeholder.tsx`, a `'pending'` section's deep link).
   *
   * It is NOT a sidebar tooltip, and must not become one. The sidebar shows
   * only `label`, and only in the collapsed rail, where the question is
   * «what is this icon?» (`layout/sidebar.tsx`); a sentence there answered it
   * with a paragraph, and AntD also copied it onto the row as a native title.
   */
  description: string;
  group: NavGroupKey;
  /**
   * A name from the shared Iconsax registry, not a component.
   *
   * The sidebar renders it with `<Icon>`, which is what keeps the whole panel
   * on Linear-for-nav / Bold-for-active and keeps any horizontal glyph
   * direction-aware. Holding a component here would let a section smuggle in a
   * second icon set — which is how this file came to import twelve
   * `@ant-design/icons` glyphs in the first place.
   */
  icon: IconName;
  /** Single permission gate. */
  permission?: Permission;
  /** Any-of gate, for a screen several roles reach through different keys. */
  anyOf?: readonly Permission[];
  status: SectionStatus;
  /** Features of this section's plan-locked tabs — the sidebar's «پلن بالاتر» badge. */
  planFeatures?: readonly EnforcedPlanFeature[];
  /** One plan feature the WHOLE route needs; the router wraps it in `RequirePlanFeature`. */
  planFeature?: EnforcedPlanFeature;
  /** Lazy module for a `'ready'` section. */
  load?: () => Promise<{ default: ComponentType }>;
}

export const SECTIONS: readonly SectionDef[] = [
  {
    key: 'dashboard',
    path: '/',
    label: 'داشبورد',
    description: 'نمای امروز کلینیک: نوبت‌ها، پیگیری‌ها، رضایت‌نامه‌ها، انبار و درآمد.',
    group: 'overview',
    icon: 'dashboard',
    status: 'ready',
    load: () => import('@/features/dashboard/dashboard-page'),
  },
  {
    key: 'insights',
    path: '/insights',
    label: 'بینش‌ها',
    description: 'بازدید صفحهٔ عمومی و نوبت‌های آنلاین',
    group: 'overview',
    icon: 'trend-up',
    // Counts only — no patient is named anywhere on the screen. Owner by
    // default; a custom role gains it in «کارکنان و نقش‌ها». No plan gate:
    // insights are reads, and a plan never refuses a read.
    permission: Permission.INSIGHTS_VIEW,
    status: 'ready',
    load: () => import('@/features/insights/insights-page'),
  },
  {
    key: 'calendar',
    path: '/calendar',
    label: 'تقویم نوبت‌ها',
    description: 'نمای روز و هفته، رزرو، جابه‌جایی و تأیید درخواست‌های آنلاین.',
    group: 'overview',
    icon: 'calendar',
    permission: Permission.APPOINTMENT_VIEW,
    status: 'ready',
    load: () => import('@/features/calendar/calendar-page'),
  },
  {
    key: 'till',
    path: '/till',
    label: 'صندوق',
    description: 'دریافت‌های امروز، نقد صندوق و بستن صندوق در پایان روز.',
    group: 'overview',
    icon: 'pos',
    permission: Permission.FINANCE_RECORD_PAYMENT,
    status: 'ready',
    load: () => import('@/features/till/till-page'),
  },
  {
    key: 'patients',
    path: '/patients',
    label: 'بیماران',
    description: 'پروندهٔ بیماران، سوابق درمان، یادداشت‌ها و برچسب‌ها.',
    group: 'care',
    icon: 'patients',
    permission: Permission.PATIENT_VIEW,
    status: 'ready',
    load: () => import('@/features/patients/patients-page'),
  },
  {
    key: 'sessions',
    path: '/sessions',
    label: 'جلسات درمان',
    description: 'ثبت جلسات درمان، نقشهٔ تزریق، مواد مصرفی و پرداخت بیمار.',
    group: 'care',
    icon: 'procedure',
    permission: Permission.SESSION_VIEW,
    status: 'ready',
    load: () => import('@/features/sessions/sessions-page'),
  },
  {
    key: 'photos',
    path: '/photos',
    label: 'عکس‌های قبل و بعد',
    description: 'عکس‌های بیماران و مقایسهٔ قبل و بعد درمان. هر بار مشاهدهٔ عکس‌ها ثبت می‌شه.',
    group: 'care',
    icon: 'camera',
    permission: Permission.PHOTO_VIEW,
    status: 'ready',
    load: () => import('@/features/photos/photos-page'),
  },
  {
    key: 'procedures',
    path: '/procedures',
    label: 'خدمات و تعرفه‌ها',
    description: 'فهرست خدمات کلینیک، مدت، قیمت و تنظیمات یادآوری.',
    group: 'operations',
    icon: 'category',
    permission: Permission.PROCEDURE_VIEW,
    status: 'ready',
    load: () => import('@/features/procedures/procedures-page'),
  },
  {
    key: 'packages',
    path: '/packages',
    label: 'بسته‌های خدمات',
    description: 'بسته‌های خدمات چندجلسه‌ای، فروش و جلسات باقی‌ماندهٔ بیماران.',
    group: 'operations',
    icon: 'gift',
    permission: Permission.PACKAGE_VIEW,
    status: 'ready',
    load: () => import('@/features/packages/packages-page'),
  },
  {
    key: 'inventory',
    path: '/inventory',
    label: 'انبار',
    description: 'کالاها، تاریخ انقضا، ورود کالا و ثبت مصرف.',
    group: 'operations',
    icon: 'inventory',
    permission: Permission.INVENTORY_VIEW,
    planFeatures: sectionTabFeatures('inventory'),
    status: 'ready',
    load: () => import('@/features/inventory/inventory-page'),
  },
  {
    key: 'staff',
    path: '/staff',
    label: 'کارکنان و نقش‌ها',
    description: 'مشخصات کارکنان، دعوت همکار جدید و سطح دسترسی هر نقش.',
    group: 'operations',
    icon: 'staff',
    permission: Permission.STAFF_VIEW,
    status: 'ready',
    load: () => import('@/features/staff/staff-page'),
  },
  {
    key: 'accounting',
    path: '/accounting',
    label: 'حسابداری',
    description: 'درآمد، هزینه، حقوق، سهم پزشکان و صورت سود و زیان.',
    group: 'finance',
    icon: 'chart',
    anyOf: [
      Permission.FINANCE_VIEW_REVENUE,
      Permission.FINANCE_VIEW_COSTS,
      Permission.FINANCE_VIEW_PROFIT,
    ],
    planFeatures: sectionTabFeatures('accounting'),
    status: 'ready',
    load: () => import('@/features/accounting/accounting-page'),
  },
  {
    key: 'crm',
    path: '/crm',
    // Not «سرنخ‌ها و کمپین»: «سرنخ» (lead) is sales jargon a clinic manager
    // does not use. And the glyph is a message, not the consent form it was.
    // Short enough for the sidebar: «درخواست‌های مشاوره و پیامک» was cut off,
    // and the group heading «ارتباط با بیماران» already says whose requests.
    label: 'مشاوره و پیامک',
    description:
      'کسانی که تماس گرفته‌اند یا مشاوره خواسته‌اند، پیگیری اون‌ها، و ارسال پیامک گروهی.',
    group: 'engagement',
    icon: 'chat',
    // Any one of the keys a CRM tab is built on, not «سرنخ‌ها» alone: a
    // receptionist who sends the SMS campaigns, checks loyalty points or reads
    // survey answers (`patient.view`) without handling leads had no way into
    // the section, although every tab she could use was already hers. The
    // page itself opens on the first tab the caller may see.
    anyOf: [
      Permission.CRM_VIEW_LEADS,
      Permission.CRM_SEND_CAMPAIGNS,
      Permission.LOYALTY_VIEW,
      Permission.PATIENT_VIEW,
    ],
    planFeatures: sectionTabFeatures('crm'),
    status: 'ready',
    load: () => import('@/features/crm/crm-page'),
  },
  {
    key: 'reviews',
    path: '/reviews',
    label: 'نظرات بیماران',
    description: 'نظرهای بیماران در صفحهٔ عمومی کلینیک و پاسخ کلینیک به اون‌ها.',
    group: 'engagement',
    icon: 'star',
    permission: Permission.DIRECTORY_MANAGE,
    status: 'ready',
    load: () => import('@/features/reviews/reviews-page'),
  },
  {
    key: 'settings',
    path: '/settings',
    label: 'تنظیمات',
    description: 'مشخصات کلینیک، ساعات کاری، اشتراک، اعتبار پیامک و امنیت حساب.',
    group: 'administration',
    icon: 'settings',
    permission: Permission.SETTINGS_VIEW,
    planFeatures: sectionTabFeatures('settings'),
    status: 'ready',
    load: () => import('@/features/settings/settings-page'),
  },
];

export function findSectionByPath(pathname: string): SectionDef | undefined {
  if (pathname === '/' || pathname === '') return SECTIONS.find((s) => s.path === '/');
  // Longest match wins, so `/patients/abc` resolves to `patients`, not to the
  // dashboard's `/`.
  return SECTIONS.filter((section) => section.path !== '/')
    .filter((section) => pathname === section.path || pathname.startsWith(`${section.path}/`))
    .sort((a, b) => b.path.length - a.path.length)[0];
}

export function findSectionByKey(key: string): SectionDef | undefined {
  return SECTIONS.find((section) => section.key === key);
}

/** Every permission that opens a section, whichever of the two gates it uses. */
export function sectionPermissions(section: SectionDef): readonly Permission[] {
  if (section.permission) return [section.permission];
  return section.anyOf ?? [];
}

/**
 * Whether a caller holding `permissions` can open the section's screen — the
 * same test `RequirePermission` applies to its route. The breadcrumb uses it
 * so it never LINKS to a page that answers «دسترسی ندارید».
 */
export function canOpenSection(section: SectionDef, permissions: readonly string[]): boolean {
  if (section.permission) return hasPermission(permissions, section.permission);
  if (section.anyOf && section.anyOf.length > 0)
    return hasAnyPermission(permissions, section.anyOf);
  return true;
}
